import React, {
  type ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";

import {
  api,
  setToken,
  setStoredUser,
  getStoredUser,
} from "../api";

import type {
  AuthUser,
  EffectivePermission,
  Id,
  OperationalAuthSession,
  Organization,
  OrganizationAccess,
  OrganizationLocation,
  OrganizationMembership,
  OrganizationSelectionSession,
  PermissionScope,
} from "../types";

const ORGANIZATION_KEY =
  "mauvelore_organization";
const MEMBERSHIP_KEY =
  "mauvelore_membership";
const LOCATIONS_KEY =
  "mauvelore_locations";
const ACTIVE_LOCATION_KEY =
  "mauvelore_active_location";
const SELECTION_KEY =
  "mauvelore_organization_selection";

function readStorage<T>(
  storage: Storage,
  key: string
): T | null {
  const raw =
    storage.getItem(key);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

function writeStorage(
  storage: Storage,
  key: string,
  value: unknown | null
) {
  if (value === null) {
    storage.removeItem(key);
    return;
  }

  storage.setItem(
    key,
    JSON.stringify(value)
  );
}

function storedOrganization() {
  return readStorage<Organization>(
    localStorage,
    ORGANIZATION_KEY
  );
}

function storedMembership() {
  return readStorage<OrganizationMembership>(
    localStorage,
    MEMBERSHIP_KEY
  );
}

function storedLocations() {
  return (
    readStorage<OrganizationLocation[]>(
      localStorage,
      LOCATIONS_KEY
    ) || []
  );
}

function storedActiveLocation() {
  return readStorage<OrganizationLocation>(
    localStorage,
    ACTIVE_LOCATION_KEY
  );
}

function storedSelection() {
  return readStorage<OrganizationSelectionSession>(
    sessionStorage,
    SELECTION_KEY
  );
}

interface AuthCtx {
  user: AuthUser | null;
  organization: Organization | null;
  membership: OrganizationMembership | null;
  locations: OrganizationLocation[];
  activeLocation: OrganizationLocation | null;
  organizations: OrganizationAccess[];
  selectionSession: OrganizationSelectionSession | null;
  permissions: EffectivePermission[];
  permissionsLoading: boolean;
  switchingOrganization: boolean;
  switchingLocation: boolean;

  login: (
    email: string,
    password: string
  ) => Promise<void>;

  selectOrganization: (
    membershipId: Id
  ) => Promise<void>;

  switchOrganization: (
    membershipId: Id
  ) => Promise<void>;

  switchLocation: (
    locationId: Id | null
  ) => Promise<void>;

  logout: () => void;

  hasPermission: (
    code: string
  ) => boolean;

  permissionScope: (
    code: string
  ) => PermissionScope | null;

  isAdminLike: boolean;
}

const AuthContext =
  createContext<AuthCtx>({
    user: null,
    organization: null,
    membership: null,
    locations: [],
    activeLocation: null,
    organizations: [],
    selectionSession: null,
    permissions: [],
    permissionsLoading: false,
    switchingOrganization: false,
    switchingLocation: false,
    login: async () => {},
    selectOrganization: async () => {},
    switchOrganization: async () => {},
    switchLocation: async () => {},
    logout: () => {},
    hasPermission: () => false,
    permissionScope: () => null,
    isAdminLike: false,
  });

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [user, setUser] =
    useState<AuthUser | null>(
      getStoredUser()
    );

  const [
    organization,
    setOrganization,
  ] =
    useState<Organization | null>(
      storedOrganization()
    );

  const [
    membership,
    setMembership,
  ] =
    useState<OrganizationMembership | null>(
      storedMembership()
    );

  const [
    locations,
    setLocations,
  ] =
    useState<OrganizationLocation[]>(
      storedLocations()
    );

  const [
    activeLocation,
    setActiveLocation,
  ] =
    useState<OrganizationLocation | null>(
      storedActiveLocation()
    );

  const [
    organizations,
    setOrganizations,
  ] =
    useState<OrganizationAccess[]>([]);

  const [
    selectionSession,
    setSelectionSession,
  ] =
    useState<OrganizationSelectionSession | null>(
      storedSelection()
    );

  const [
    permissions,
    setPermissions,
  ] =
    useState<EffectivePermission[]>([]);

  const [
    permissionsLoading,
    setPermissionsLoading,
  ] =
    useState(false);

  const [
    switchingOrganization,
    setSwitchingOrganization,
  ] =
    useState(false);

  const [
    switchingLocation,
    setSwitchingLocation,
  ] =
    useState(false);

  const clearOperationalSession =
    useCallback(() => {
      setToken(null);
      setStoredUser(null);

      writeStorage(
        localStorage,
        ORGANIZATION_KEY,
        null
      );
      writeStorage(
        localStorage,
        MEMBERSHIP_KEY,
        null
      );
      writeStorage(
        localStorage,
        LOCATIONS_KEY,
        null
      );
      writeStorage(
        localStorage,
        ACTIVE_LOCATION_KEY,
        null
      );

      setUser(null);
      setOrganization(null);
      setMembership(null);
      setLocations([]);
      setActiveLocation(null);
      setOrganizations([]);
      setPermissions([]);
    }, []);

  const clearSelection =
    useCallback(() => {
      writeStorage(
        sessionStorage,
        SELECTION_KEY,
        null
      );
      setSelectionSession(null);
    }, []);

  const applyOperationalSession =
    useCallback(
      (
        session:
          OperationalAuthSession
      ) => {
        setToken(session.token);
        setStoredUser(session.user);

        const nextOrganization =
          session.organization || null;
        const nextMembership =
          session.membership || null;
        const nextLocations =
          session.locations || [];
        const nextActiveLocation =
          session.active_location ||
          null;

        writeStorage(
          localStorage,
          ORGANIZATION_KEY,
          nextOrganization
        );
        writeStorage(
          localStorage,
          MEMBERSHIP_KEY,
          nextMembership
        );
        writeStorage(
          localStorage,
          LOCATIONS_KEY,
          nextLocations
        );
        writeStorage(
          localStorage,
          ACTIVE_LOCATION_KEY,
          nextActiveLocation
        );

        clearSelection();

        setUser(session.user);
        setOrganization(
          nextOrganization
        );
        setMembership(
          nextMembership
        );
        setLocations(
          nextLocations
        );
        setActiveLocation(
          nextActiveLocation
        );
        setPermissions(
          session.permissions || []
        );
      },
      [
        clearSelection,
      ]
    );

  const loadPermissions =
    useCallback(async () => {
      if (!user) {
        setPermissions([]);
        return;
      }

      setPermissionsLoading(true);

      try {
        const result =
          await api.authPermissions();

        setPermissions(
          result.permissions || []
        );
      } catch {
        setPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    }, [
      user,
      membership?.id,
      organization?.id,
    ]);

  useEffect(() => {
    loadPermissions();
  }, [
    loadPermissions,
  ]);

  useEffect(() => {
    if (
      !user ||
      !organization
    ) {
      setOrganizations([]);
      return;
    }

    let cancelled =
      false;

    api.authOrganizations()
      .then((result) => {
        if (!cancelled) {
          setOrganizations(
            result.organizations || []
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOrganizations([]);
        }
      });

    return () => {
      cancelled =
        true;
    };
  }, [
    user,
    organization?.id,
  ]);

  const login =
    useCallback(
      async (
        email: string,
        password: string
      ) => {
        clearOperationalSession();
        clearSelection();

        const response =
          await api.login(
            email,
            password
          );

        if (!("token" in response)) {
          writeStorage(
            sessionStorage,
            SELECTION_KEY,
            response
          );
          setSelectionSession(
            response
          );
          return;
        }

        applyOperationalSession(
          response
        );
      },
      [
        applyOperationalSession,
        clearOperationalSession,
        clearSelection,
      ]
    );

  const selectOrganization =
    useCallback(
      async (
        membershipId: Id
      ) => {
        if (!selectionSession) {
          throw new Error(
            "No hay una selección organizacional pendiente."
          );
        }

        const session =
          await api.selectOrganization(
            selectionSession.selection_token,
            membershipId
          );

        applyOperationalSession(
          session
        );
      },
      [
        applyOperationalSession,
        selectionSession,
      ]
    );

  const switchOrganization =
    useCallback(
      async (
        membershipId: Id
      ) => {
        if (
          String(membership?.id) ===
          String(membershipId)
        ) {
          return;
        }

        setSwitchingOrganization(
          true
        );

        try {
          const session =
            await api.switchOrganization(
              membershipId
            );

          applyOperationalSession(
            session
          );

          window.location.reload();
        } finally {
          setSwitchingOrganization(
            false
          );
        }
      },
      [
        applyOperationalSession,
        membership?.id,
      ]
    );

  const switchLocation =
    useCallback(
      async (
        locationId: Id | null
      ) => {
        if (
          String(
            activeLocation?.id ??
            ""
          ) ===
          String(
            locationId ??
            ""
          )
        ) {
          return;
        }

        setSwitchingLocation(
          true
        );

        try {
          const session =
            await api.switchLocation(
              locationId
            );

          applyOperationalSession(
            session
          );

          window.location.reload();
        } finally {
          setSwitchingLocation(
            false
          );
        }
      },
      [
        activeLocation?.id,
        applyOperationalSession,
      ]
    );

  const logout =
    useCallback(() => {
      clearSelection();
      clearOperationalSession();
    }, [
      clearOperationalSession,
      clearSelection,
    ]);

  const permissionMap =
    useMemo(
      () =>
        new Map(
          permissions.map(
            (permission) => [
              permission.code,
              permission,
            ]
          )
        ),
      [
        permissions,
      ]
    );

  const hasPermission =
    useCallback(
      (code: string) =>
        permissionMap.get(code)
          ?.allowed === true,
      [
        permissionMap,
      ]
    );

  const permissionScope =
    useCallback(
      (
        code: string
      ): PermissionScope | null => {
        const permission =
          permissionMap.get(code);

        return permission?.allowed
          ? permission.scope
          : null;
      },
      [
        permissionMap,
      ]
    );

  const isAdminLike =
    hasPermission(
      "quotes.approve_override"
    ) ||
    hasPermission(
      "settlements.manage"
    ) ||
    hasPermission(
      "economics.costs.view"
    );

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        membership,
        locations,
        activeLocation,
        organizations,
        selectionSession,
        permissions,
        permissionsLoading,
        switchingOrganization,
        switchingLocation,
        login,
        selectOrganization,
        switchOrganization,
        switchLocation,
        logout,
        hasPermission,
        permissionScope,
        isAdminLike,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(
    AuthContext
  );
}
